import type { IPositionMeasurement } from '@lucimark/shared/models/geolocation'
import type { IGeolocationSample, IKalman } from '@/services/kalman'
import type { Logger } from '@/services/logger'
import { distance, point } from '@turf/turf'
import Emittery from 'emittery'
import { generate as generateShortUuid } from 'short-uuid'
import { shouldUplinkGeolocationSample } from '@/helpers/shouldUplinkGeolocationSample'
import {
  scheduleClearOrphanNativeGeoWatch,
  withNativeGeoIpcTimeout
} from '@/helpers/withNativeGeoIpcTimeout'
import { getPlatform } from '@/platform'

export type GeolocationServiceCallback = (update: IPositionMeasurement) => void | Promise<void>

export interface IGeolocationService {
  start: () => void | Promise<void>
  stop: () => void
  subscribe: (callback: GeolocationServiceCallback) => string
  unsubscribe: (subscriptionId: string) => void
  lastReportedPosition: IPositionMeasurement | null
}

/**
 * Build a `GeolocationPositionError`-compatible object so both the browser and the
 * Tauri native backend can surface errors through the same event and telemetry path
 * (`usePlayer` forwards codes 1/2/3 to the API). The Tauri plugin has no native error
 * codes, so permission denial maps to `PERMISSION_DENIED` (1) and everything else to
 * `POSITION_UNAVAILABLE` (2).
 */
function makeGeolocationError(code: 1 | 2 | 3, message: string): GeolocationPositionError {
  return {
    code,
    message,
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3
  } as GeolocationPositionError
}

export enum GeolocationServiceEvent {
  RUNNING = 'running',
  POSITION = 'position',
  POSITION_DMS = 'position_dms',
  POSITION_UPDATES = 'position_updates',
  ERROR = 'error'
}

export interface GeolocationServiceEventMap {
  [GeolocationServiceEvent.RUNNING]: boolean
  [GeolocationServiceEvent.POSITION]: IPositionMeasurement | null
  [GeolocationServiceEvent.POSITION_DMS]: string | null
  [GeolocationServiceEvent.POSITION_UPDATES]: number
  [GeolocationServiceEvent.ERROR]: GeolocationPositionError | null
}

function toDegreesMinutesAndSeconds(coordinate: number) {
  const absolute = Math.abs(coordinate)
  const degrees = Math.floor(absolute)
  const minutesNotTruncated = (absolute - degrees) * 60
  const minutes = Math.floor(minutesNotTruncated)
  const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(1)
  const dms = `${degrees}°${minutes}'${seconds}''`
  return dms
}

/** Accept OS cached fixes up to 5 min when bootstrapping before the watch starts. */
const NATIVE_BOOTSTRAP_MAXIMUM_AGE_MS = 300_000
/** Ongoing watch: allow fused/network last-known for indoor (1 min). */
const NATIVE_WATCH_MAXIMUM_AGE_MS = 60_000
const NATIVE_GEO_TIMEOUT_MS = 30_000

function convertDMS(coords: { lat: number, lng: number }) {
  const { lat, lng } = coords
  const latitude = toDegreesMinutesAndSeconds(lat)
  const latitudeCardinal = lat >= 0 ? 'N' : 'S'

  const longitude = toDegreesMinutesAndSeconds(lng)
  const longitudeCardinal = lng >= 0 ? 'E' : 'W'
  const dms = `${latitude}${latitudeCardinal} ${longitude}${longitudeCardinal}`
  return dms
}

export default class GeolocationService extends Emittery<GeolocationServiceEventMap> implements IGeolocationService {
  /** Browser `navigator.geolocation.watchPosition` id (web / desktop dev). */
  private watchId: number | null = null
  /** Tauri geolocation plugin watch channel id (Android). */
  private nativeWatchId: number | null = null
  private readonly subscriptions: Record<string, GeolocationServiceCallback> = {}
  private readonly kalman: IKalman
  private _lastReportedPosition: IPositionMeasurement | null = null
  private _lastReportedPositionDMS: string | null = null
  private logger: Logger
  private _error: GeolocationPositionError | null = null
  private _positionUpdates: number = 0
  /** Decides the geolocation backend; defaults to the Tauri plugin on Android. */
  private readonly useNativeGeolocation: () => boolean

  constructor(params: { kalman: IKalman, logger: Logger, useNativeGeolocation?: () => boolean }) {
    super()
    const { kalman, logger, useNativeGeolocation } = params
    this.kalman = kalman
    this.logger = logger.child({ source: GeolocationService.name })
    this.useNativeGeolocation = useNativeGeolocation ?? (() => getPlatform() === 'android')
  }

  get error() {
    return this._error
  }

  set error(error: GeolocationPositionError | null) {
    this._error = error
    void this.emit(GeolocationServiceEvent.ERROR, this._error)
  }

  get lastReportedPosition() {
    return this._lastReportedPosition
  }

  set lastReportedPosition(position: IPositionMeasurement | null) {
    this._positionUpdates++
    this._lastReportedPosition = position
    if (position === null) {
      this._lastReportedPositionDMS = null
    }
    else {
      const { latitude: lat, longitude: lng } = position
      this._lastReportedPositionDMS = convertDMS({ lat, lng })
    }
    void this.emit(GeolocationServiceEvent.POSITION_UPDATES, this._positionUpdates)
    void this.emit(GeolocationServiceEvent.POSITION_DMS, this._lastReportedPositionDMS)
    void this.emit(GeolocationServiceEvent.POSITION, this._lastReportedPosition)
  }

  get lastReportedPositionDMS() {
    return this._lastReportedPositionDMS
  }

  get positionUpdates() {
    return this._positionUpdates
  }

  /**
   * Shared pipeline for both backends: Kalman-filter, then report when the move
   * exceeds accuracy, or when the last uplink is older than Studio's freshness window.
   */
  private handleSample(sample: IGeolocationSample) {
    const measurement = this.kalman.update(sample)
    const lastMeasurementSent = this.lastReportedPosition
    const d
      = lastMeasurementSent === null
        ? null
        : distance(
            point([measurement.longitude, measurement.latitude]),
            point([lastMeasurementSent.longitude, lastMeasurementSent.latitude]),
            { units: 'meters' }
          )
    if (shouldUplinkGeolocationSample({
      lastReported: lastMeasurementSent,
      distanceMeters: d,
      sampleAccuracyMeters: sample.coords.accuracy,
      nowMs: sample.timestamp
    })) {
      this.lastReportedPosition = measurement
      void Promise.all(Object.values(this.subscriptions).map(async callback => callback(measurement)))
    }
  }

  private positionUpdateHandler: PositionCallback = position => this.handleSample(position)

  private positionErrorCallback: PositionErrorCallback = (positionError: GeolocationPositionError) => {
    this.error = positionError
    this.logger.error(`Position error: ${JSON.stringify(positionError.message)}`)
  }

  async start() {
    if (this.watchId !== null || this.nativeWatchId !== null) {
      // Keep overlay in sync if Vue state was reset without clearing the native watch ids.
      void this.emit(GeolocationServiceEvent.RUNNING, true)
      return
    }
    if (this.useNativeGeolocation()) {
      await this.startNative()
    }
    else {
      this.startBrowser()
    }
  }

  private startBrowser() {
    if (typeof navigator === 'undefined' || navigator.geolocation === undefined) {
      this.error = makeGeolocationError(2, 'Geolocation API unavailable')
      this.logger.error('navigator.geolocation is unavailable')
      return
    }
    navigator.geolocation.getCurrentPosition(
      this.positionUpdateHandler,
      () => {
        // Non-fatal — watch continues.
      },
      {
        enableHighAccuracy: true,
        maximumAge: NATIVE_BOOTSTRAP_MAXIMUM_AGE_MS,
        timeout: NATIVE_GEO_TIMEOUT_MS
      }
    )
    this.watchId = navigator.geolocation.watchPosition(this.positionUpdateHandler, this.positionErrorCallback, {
      enableHighAccuracy: true,
      maximumAge: NATIVE_WATCH_MAXIMUM_AGE_MS
    })
    void this.emit(GeolocationServiceEvent.RUNNING, true)
    this.logger.debug('started browser geolocation service')
  }

  private async bootstrapNativeLastKnown(
    geo: typeof import('@tauri-apps/plugin-geolocation')
  ) {
    try {
      const position = await withNativeGeoIpcTimeout(
        geo.getCurrentPosition({
          enableHighAccuracy: true,
          maximumAge: NATIVE_BOOTSTRAP_MAXIMUM_AGE_MS,
          timeout: NATIVE_GEO_TIMEOUT_MS
        }),
        'getCurrentPosition'
      )
      if (position != null) {
        this.error = null
        this.handleSample(position)
      }
    }
    catch (err) {
      this.logger.debug({ err }, 'native geolocation bootstrap getCurrentPosition skipped')
    }
  }

  /**
   * Android backend: the WebView `navigator.geolocation` never fires without a native permission
   * grant, so use the Tauri geolocation plugin, which declares the manifest permissions and drives
   * the OS prompt (issue #1136).
   *
   * Emit RUNNING as soon as permissions are granted (before `watchPosition` IPC returns) so the
   * status overlay shows "aguardando..." instead of a false "desativado" while the first fix is
   * pending — bootstrap + bounded `maximumAge` on the watch reuse indoor fused last-known fixes.
   *
   * IPC calls that should return promptly are bounded with {@link withNativeGeoIpcTimeout} so a
   * hung SlimBOX plugin cannot leave the overlay on "desativado" forever with no error.
   */
  private async startNative() {
    try {
      const geo = await withNativeGeoIpcTimeout(
        import('@tauri-apps/plugin-geolocation'),
        'import'
      )
      let permissions = await withNativeGeoIpcTimeout(
        geo.checkPermissions(),
        'checkPermissions'
      )
      if (permissions.location === 'prompt' || permissions.location === 'prompt-with-rationale') {
        // User may take arbitrarily long on the OS dialog — do not IPC-timeout this await.
        permissions = await geo.requestPermissions(['location'])
      }
      if (permissions.location !== 'granted') {
        this.error = makeGeolocationError(1, `Location permission ${permissions.location}`)
        this.logger.error(`native geolocation permission not granted: ${permissions.location}`)
        void this.emit(GeolocationServiceEvent.RUNNING, false)
        return
      }
      this.error = null
      void this.emit(GeolocationServiceEvent.RUNNING, true)
      await this.bootstrapNativeLastKnown(geo)
      const watchPromise = geo.watchPosition(
        { enableHighAccuracy: true, timeout: NATIVE_GEO_TIMEOUT_MS, maximumAge: NATIVE_WATCH_MAXIMUM_AGE_MS },
        (position, error) => {
          if (error != null) {
            this.error = makeGeolocationError(2, error)
            this.logger.error(`native geolocation watch error: ${error}`)
            return
          }
          if (position != null) {
            this.error = null
            this.handleSample(position)
          }
        }
      )
      try {
        this.nativeWatchId = await withNativeGeoIpcTimeout(watchPromise, 'watchPosition')
      }
      catch (watchErr) {
        // Timeout abandons the await; if the native channel id arrives later, clear it.
        scheduleClearOrphanNativeGeoWatch({
          watchPromise,
          clearWatch: channelId => geo.clearWatch(channelId),
          isOwnedChannel: channelId => this.nativeWatchId === channelId
        })
        throw watchErr
      }
      this.logger.debug('started native geolocation watch')
    }
    catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      const timedOut = message.includes('Geolocation IPC timeout')
      this.error = makeGeolocationError(timedOut ? 3 : 2, message)
      this.logger.error(`failed to start native geolocation: ${message}`)
      void this.emit(GeolocationServiceEvent.RUNNING, false)
    }
  }

  stop() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.watchId = null
      void this.emit(GeolocationServiceEvent.RUNNING, false)
      this.logger.debug('stopped browser geolocation service')
      return
    }
    if (this.nativeWatchId !== null) {
      const channelId = this.nativeWatchId
      this.nativeWatchId = null
      void import('@tauri-apps/plugin-geolocation')
        .then(async geo => geo.clearWatch(channelId))
        .catch(() => {
          // best-effort teardown; the watch is cleared on process exit anyway
        })
      void this.emit(GeolocationServiceEvent.RUNNING, false)
      this.logger.debug('stopped native geolocation watch')
    }
  }

  subscribe(callback: GeolocationServiceCallback) {
    const subscriptionId = generateShortUuid()
    this.subscriptions[subscriptionId] = callback
    return subscriptionId
  }

  unsubscribe(subscriptionId: string) {
    delete this.subscriptions[subscriptionId]
  }
}
